import * as Sentry from '@sentry/react'
import { dynamicImportErrors } from './SiteMetricsErrorBoundary'

const isEnvE2E = window.origin.includes('e2e')

export type SampleRate = number
export type SampleRules = {
  [match: string]: SampleRate
  defaultSampleRate: SampleRate
}

export const getSampleRateMatchingNameOrDefault = (name: string, rules: SampleRules) => {
  const matches = Object.keys(rules)
  const matchingKey = matches.find((it) => name.toLowerCase().indexOf(it.toLowerCase()) > -1)
  if (matchingKey) {
    return rules[matchingKey]
  } else {
    return rules.defaultSampleRate
  }
}

export const initializeSiteMetrics = (
  apiKey: string,
  environment: string,
  sampleRules?: SampleRules,
  transport?: any
) => {
  // internal-frontend's master build puts its release in index.html, so its hashed chunks stay the same across commits
  const release = document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')?.content
  Sentry.init({
    dsn: apiKey,
    environment,
    ...(release ? { release } : {}),
    transport,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampler: (context) => {
      if (sampleRules) {
        return context.parentSampled ? 1 : getSampleRateMatchingNameOrDefault(context.name, sampleRules)
      } else {
        return 0
      }
    },
    beforeSend(event) {
      if (
        event.breadcrumbs?.some((breadcrump) =>
          dynamicImportErrors.some((message) => breadcrump.message?.includes(message))
        )
      ) {
        return null
      }

      if (
        event.exception?.values?.some(
          (exceptionValue) =>
            exceptionValue.type === 'UnhandledRejection' &&
            exceptionValue.value?.includes('Object captured as promise rejection with keys')
        )
      ) {
        return null
      }

      if (event.exception?.values?.some((exceptionValue) => exceptionValue.value?.includes('_leaflet_pos'))) {
        return null
      }

      if (isEnvE2E) return null
      return event
    }
  })
}

export const siteMetricsCaptureErrorMessage = (message: string, context: object) =>
  Sentry.captureMessage(message, context)

export const siteMetricsSetUser = (id: number | undefined) => {
  Sentry.setUser(id === undefined ? null : { id })
}
